import { motion } from 'framer-motion';
import { Layout } from '../components/Layout';

const storyImages = {
  artisan: 'https://images.unsplash.com/photo-1767390552768-6703f91c2518?w=800&q=80',
  heritage: 'https://images.unsplash.com/photo-1760939399262-d4d30d78a291?w=800&q=80',
  mountains: 'https://images.unsplash.com/photo-1648566924598-aeb39df9ad79?w=800&q=80',
  texture: 'https://images.pexels.com/photos/7232494/pexels-photo-7232494.jpeg?w=800&q=80',
};

const values = [
  {
    title: 'Authentic Craftsmanship',
    description:
      'Every piece is handcrafted by skilled artisans using techniques passed down through generations. We preserve the ancient art of Himalayan weaving.',
  },
  {
    title: 'Premium Materials',
    description:
      'We source only the finest Grade A cashmere and virgin wool from high-altitude Himalayan regions, ensuring exceptional softness and durability.',
  },
  {
    title: 'Sustainable Practices',
    description:
      'Our commitment to sustainability means ethical sourcing, fair wages for artisans, and environmentally conscious production methods.',
  },
  {
    title: 'Timeless Design',
    description:
      'We create pieces that transcend trends—elegant, versatile garments designed to be treasured for years to come.',
  },
];

export const AboutPage = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] overflow-hidden" data-testid="about-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${storyImages.mountains})` }}
        >
          <div className="absolute inset-0 bg-[#2C2420]/50" />
        </div>
        <div className="relative h-full container-luxury flex items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs uppercase tracking-[0.3em] text-[#E6DCC8] mb-4">
              Our Story
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#F9F8F6] max-w-3xl">
              The Spirit of <span className="italic">Shiva Shakti</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section-spacing" data-testid="philosophy-section">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-xs uppercase tracking-[0.2em] text-[#C6A87C] mb-3">
                Our Philosophy
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#2C2420] mb-6">
                Where Heritage Meets Elegance
              </h2>
              <div className="space-y-6 text-[#5D5550] leading-relaxed">
                <p>
                  Shiva Shakti represents the sacred balance of opposing forces—strength and 
                  grace, tradition and innovation, earth and spirit. Our brand embodies this 
                  philosophy in every thread.
                </p>
                <p className="font-accent text-xl italic text-[#2C2420]">
                  "We believe that true luxury lies not in excess, but in the purity of 
                  materials, the skill of human hands, and the stories woven into each garment."
                </p>
                <p>
                  Founded with a vision to bring Himalayan craftsmanship to the modern world, 
                  we partner with artisan communities who have perfected their craft over 
                  centuries. Each piece carries the warmth of human touch and the wisdom of 
                  ancient traditions.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={storyImages.artisan}
                alt="Artisan weaving"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-[#C6A87C] hidden lg:block" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-spacing bg-[#F2EFE9]" data-testid="values-section">
        <div className="container-luxury">
          <div className="text-center mb-16">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-xs uppercase tracking-[0.2em] text-[#C6A87C] mb-3"
            >
              What We Stand For
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-heading text-3xl md:text-4xl text-[#2C2420]"
            >
              Our Values
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="border-l-2 border-[#C6A87C] pl-6"
              >
                <h3 className="font-heading text-xl text-[#2C2420] mb-3">
                  {value.title}
                </h3>
                <p className="text-[#5D5550] leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Craftsmanship Section */}
      <section className="section-spacing" data-testid="craftsmanship-section">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="grid grid-cols-2 gap-4">
                <img
                  src={storyImages.heritage}
                  alt="Heritage weaving"
                  className="w-full aspect-square object-cover"
                />
                <img
                  src={storyImages.texture}
                  alt="Cashmere texture"
                  className="w-full aspect-square object-cover mt-8"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-[#C6A87C] mb-3">
                The Process
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#2C2420] mb-6">
                From Mountain to Wardrobe
              </h2>
              <div className="space-y-6 text-[#5D5550] leading-relaxed">
                <p>
                  Our cashmere comes from Changthangi goats that roam freely in the high 
                  Himalayan plateaus, where extreme temperatures produce the world's finest, 
                  softest fibers.
                </p>
                <p>
                  Each spring, the fibers are gently combed by hand—never sheared—ensuring 
                  the comfort of the animals and the quality of the material. The raw cashmere 
                  is then cleaned, spun, and woven by master craftsmen using traditional looms.
                </p>
                <p>
                  The result is a garment of exceptional quality—lightweight yet incredibly 
                  warm, soft against the skin, and built to last for generations.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-24 bg-[#2C2420]" data-testid="quote-section">
        <div className="container-luxury text-center">
          <motion.blockquote
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <p className="font-accent text-2xl md:text-3xl italic text-[#F9F8F6] leading-relaxed mb-6">
              "In every thread, we weave a prayer. In every stitch, we honor our ancestors. 
              This is not just clothing—it is a legacy of love."
            </p>
            <cite className="text-sm uppercase tracking-widest text-[#C6A87C] not-italic">
              — Himalayan Weaver's Blessing
            </cite>
          </motion.blockquote>
        </div>
      </section>
    </Layout>
  );
};
